import { StudyConfig } from '@csp/csp-common-config';
import { getTimeZoneId } from '@csp/csp-common-date-util';
import { ApiOptions, Patient, Study } from '@csp/csp-common-model';
import { UnbookedVisitService, VisitScheduleService } from '@csp/csp-common-visits';
import { WebHcpPatientVisitEntry } from '@csp/csp-web-content-definitions';
import { largePage } from '@csp/dmdp-api-common-dto';
import { StaticContentService } from '../../../common/service/StaticContentService';
import { StudyContentService } from '../../../common/service/StudyContentService';
import { BookedPatientVisitsQueryService } from '../../patient-list/patient-list-visit/service/BookedPatientVisitsQueryService';
import { VisitRequestInfosMapper } from '../mapper/VisitRequestInfosMapper';
import { IncludeFields } from '../model/IncludeFields';
import { PatientVisits } from '../model/PatientVisits';
import { VisitRequestInfos } from '../model/VisitRequestInfos';

const getBookedAndUnbooked = async (
  study: Study,
  studyConfig: StudyConfig,
  patient: Patient,
  patientVisitTexts: WebHcpPatientVisitEntry,
  includeFields?: IncludeFields,
): Promise<PatientVisits> => {
  const bookedPatientVisitItemPage = await BookedPatientVisitsQueryService.query(
    study,
    studyConfig,
    {
      criteria: {
        patient,
      },
      includeFields,
    },
    largePage(),
  );

  const scheduleVisitsTexts = StudyContentService.getVisitScheduleContentModel();

  const unbookedVisits = await UnbookedVisitService.getUnbookedVisits({
    patient,
    study,
    patientVisitTexts,
    bookedVisits: bookedPatientVisitItemPage.bookedVisits,
    timezoneId: getTimeZoneId(),
    scheduleVisitsTexts,
    includeFields,
  });

  return {
    unbookedVisits: unbookedVisits.all,
    hasScheduledVisits: unbookedVisits.hasScheduledVisits,
    bookedPatientVisits: bookedPatientVisitItemPage.bookedVisits,
  };
};

const getCachedActiveRequestInfosByOrgIds = async (
  orgId: string[],
  studyConfig?: StudyConfig,
  apiOptions?: ApiOptions,
): Promise<VisitRequestInfos> => {
  const patientVisitTexts = StaticContentService.getGenericEntry('webHcpPatientVisit');
  const visitContent = StudyContentService.getScheduleVisitsContent();

  const orgSchedules = await VisitScheduleService.getCachedActivatedVisitSchedulesForOrganizationIds(
    orgId,
    studyConfig,
    apiOptions,
  );
  return VisitRequestInfosMapper.from(orgSchedules.getAllRequests(), visitContent, patientVisitTexts);
};

export const PatientVisitService = {
  getBookedAndUnbooked,
  getCachedActiveRequestInfosByOrgIds,
};
